import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/brand/PageHeader';
import { Panel } from '@/components/brand/Panel';
import { useApiQuery } from '@/lib/hooks';
import { EmptyState, ErrorState, LoadingState, UnauthorizedState } from '@/components/brand/States';
import { useAuth } from '@/lib/auth';

interface Budget { id: string; service: string; monthlyLimitGbp: number; alertAtPct: number; enabled: number; }
interface Channel { id: string; channelType: 'teams' | 'email' | 'sms'; name: string; enabled: number; minSeverity: 'info' | 'warning' | 'critical'; }
interface AuditEntry { id: string; actorEmail: string; action: string; targetType: string; targetId: string | null; createdAt: number; }

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: 'include', ...init });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json();
}

function BudgetsSection() {
  const qc = useQueryClient();
  const [service, setService] = useState('anthropic');
  const [limit, setLimit] = useState(100);
  const [alertAt, setAlertAt] = useState(80);

  const q = useApiQuery<{ items: Budget[] }>({
    queryKey: ['admin/budgets'],
    queryFn: () => fetchJson('/api/admin/budgets'),
  });
  const create = useMutation({
    mutationFn: () =>
      fetchJson('/api/admin/budgets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ service, monthly_limit_gbp: limit, alert_at_pct: alertAt, enabled: true }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin/budgets'] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => fetchJson(`/api/admin/budgets/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin/budgets'] }),
  });

  return (
    <Panel title="Budgets" subtitle="GBP monthly · % warn threshold">
      <form
        onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
        className="grid grid-cols-1 md:grid-cols-4 gap-3 text-small mb-6 items-end"
      >
        <label className="flex flex-col gap-1">
          <span className="eyebrow">Service</span>
          <input value={service} onChange={(e) => setService(e.target.value)} className="border border-rule px-3 py-2 bg-bg-page mono" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="eyebrow">Limit £</span>
          <input type="number" value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="border border-rule px-3 py-2 bg-bg-page num" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="eyebrow">Warn at %</span>
          <input type="number" value={alertAt} onChange={(e) => setAlertAt(Number(e.target.value))} className="border border-rule px-3 py-2 bg-bg-page num" />
        </label>
        <button type="submit" disabled={create.isPending} className="bg-fg-default text-mb-spring-wood px-4 py-2 text-eyebrow disabled:opacity-40">
          {create.isPending ? 'Adding…' : 'Add budget'}
        </button>
      </form>
      {q.isLoading && <LoadingState />}
      {q.error && <ErrorState title="Couldn't load budgets" detail={(q.error as Error).message} />}
      {q.data && q.data.items.length === 0 && <EmptyState title="No budgets yet" />}
      {q.data && q.data.items.length > 0 && (
        <table className="w-full table-fixed text-small">
          <thead className="text-fg-muted text-eyebrow"><tr><th className="text-left py-2 w-1/4">Service</th><th className="text-left w-1/4">£/mo</th><th className="text-left w-1/4">Warn %</th><th className="text-right w-1/4"></th></tr></thead>
          <tbody className="divide-y divide-rule">
            {q.data.items.map((b) => (
              <tr key={b.id}>
                <td className="py-2 mono">{b.service}</td>
                <td className="num">£{b.monthlyLimitGbp.toFixed(2)}</td>
                <td className="num">{b.alertAtPct}%</td>
                <td className="text-right">
                  <button onClick={() => del.mutate(b.id)} className="text-fg-muted text-caption hover:text-sev-crit">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

function ChannelsSection() {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [channelType, setChannelType] = useState<'teams' | 'email' | 'sms'>('teams');
  const [minSeverity, setMinSeverity] = useState<'info' | 'warning' | 'critical'>('warning');

  const q = useApiQuery<{ items: Channel[] }>({
    queryKey: ['admin/notification-channels'],
    queryFn: () => fetchJson('/api/admin/notification-channels'),
  });
  const create = useMutation({
    mutationFn: () =>
      fetchJson('/api/admin/notification-channels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel_type: channelType, name, min_severity: minSeverity, enabled: true, config: {} }),
      }),
    onSuccess: () => { setName(''); qc.invalidateQueries({ queryKey: ['admin/notification-channels'] }); },
  });
  const del = useMutation({
    mutationFn: (id: string) => fetchJson(`/api/admin/notification-channels/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin/notification-channels'] }),
  });

  return (
    <Panel title="Notification channels" subtitle="Per-channel routing">
      <form
        onSubmit={(e) => { e.preventDefault(); if (name) create.mutate(); }}
        className="grid grid-cols-1 md:grid-cols-4 gap-3 text-small mb-6 items-end"
      >
        <label className="flex flex-col gap-1">
          <span className="eyebrow">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required className="border border-rule px-3 py-2 bg-bg-page" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="eyebrow">Type</span>
          <select value={channelType} onChange={(e) => setChannelType(e.target.value as typeof channelType)} className="border border-rule px-3 py-2 bg-bg-page">
            <option value="teams">teams</option>
            <option value="email">email</option>
            <option value="sms">sms (Phase 3)</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="eyebrow">Min severity</span>
          <select value={minSeverity} onChange={(e) => setMinSeverity(e.target.value as typeof minSeverity)} className="border border-rule px-3 py-2 bg-bg-page">
            <option value="info">info</option>
            <option value="warning">warning</option>
            <option value="critical">critical</option>
          </select>
        </label>
        <button type="submit" disabled={create.isPending || !name} className="bg-fg-default text-mb-spring-wood px-4 py-2 text-eyebrow disabled:opacity-40">
          {create.isPending ? 'Adding…' : 'Add channel'}
        </button>
      </form>
      {q.isLoading && <LoadingState />}
      {q.error && <ErrorState title="Couldn't load channels" detail={(q.error as Error).message} />}
      {q.data && q.data.items.length === 0 && <EmptyState title="No channels yet" />}
      {q.data && q.data.items.length > 0 && (
        <table className="w-full table-fixed text-small">
          <thead className="text-fg-muted text-eyebrow"><tr><th className="text-left py-2 w-1/4">Name</th><th className="text-left w-1/4">Type</th><th className="text-left w-1/4">Min severity</th><th className="text-right w-1/4"></th></tr></thead>
          <tbody className="divide-y divide-rule">
            {q.data.items.map((c) => (
              <tr key={c.id}>
                <td className="py-2 truncate">{c.name}</td>
                <td className="mono">{c.channelType}</td>
                <td className="mono">{c.minSeverity}</td>
                <td className="text-right">
                  <button onClick={() => del.mutate(c.id)} className="text-fg-muted text-caption hover:text-sev-crit">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

function AuditSection() {
  const q = useApiQuery<{ items: AuditEntry[] }>({
    queryKey: ['admin/audit-log'],
    queryFn: () => fetchJson('/api/admin/audit-log?limit=100'),
  });

  return (
    <Panel title="Audit log" subtitle="Last 100 admin actions">
      {q.isLoading && <LoadingState />}
      {q.error && <ErrorState title="Couldn't load audit log" detail={(q.error as Error).message} />}
      {q.data && q.data.items.length === 0 && <EmptyState title="No admin actions yet" />}
      {q.data && q.data.items.length > 0 && (
        <table className="w-full text-small">
          <thead className="text-fg-muted text-eyebrow"><tr><th className="text-left py-2">When</th><th className="text-left">Actor</th><th className="text-left">Action</th><th className="text-left">Target</th></tr></thead>
          <tbody className="divide-y divide-rule">
            {q.data.items.map((a) => (
              <tr key={a.id}>
                <td className="py-2 mono mono-sm">{new Date(a.createdAt).toISOString()}</td>
                <td className="mono">{a.actorEmail}</td>
                <td className="mono">{a.action}</td>
                <td className="mono">{a.targetType}{a.targetId ? ` ${a.targetId.slice(0, 8)}` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

export function AdminPage() {
  const { me, isUnauthorized } = useAuth();
  if (isUnauthorized) return <UnauthorizedState />;
  if (me && me.role !== 'admin') {
    return (
      <div>
        <PageHeader eyebrow="Admin" title="Admin" />
        <Panel title="Access"><EmptyState title="Admin-only" /></Panel>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Admin" title="Admin" />
      <BudgetsSection />
      <ChannelsSection />
      <AuditSection />
    </div>
  );
}
